import './applicantResidenceDetailsPage.scss';

import { ThemeProvider } from '@mui/material/styles';
import { toCurrencyString } from '@thesavingsgroup/utils/toCurrencyString';
import { toNumericString } from '@thesavingsgroup/utils/toNumericString';
import isEmpty from 'lodash/isEmpty';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AddressLineOne } from '../../../components/AddressLineOne/AddressLineOne';
import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { Input } from '../../../components/Input/Input';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { NumberInput } from '../../../components/NumberInput/NumberInput';
import { Select } from '../../../components/Select/Select';
import { USStates } from '../../../store/addressSuggestionStore/constants/states';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    computed: { progress },
    next,
    previous,
  } = useWorkflowStore();

  const {
    computed: {
      currentResidenceCityAsString,
      currentResidenceLineOneAsString,
      currentResidenceLineTwoAsString,
      currentResidencePostalCodeAsString,
      currentResidenceStateAsString,
      isCurrentResidencePersisted,
    },
    currentResidenceMonthlyPaymentAmount,
    isSyncingResidence,
    syncingResidenceFailedMessage,
    setCurrentResidenceCity,
    setCurrentResidenceLineOne,
    setCurrentResidenceLineTwo,
    setCurrentResidenceMonthlyPaymentAmount,
    setCurrentResidencePostalCode,
    setCurrentResidenceState,
    syncResidence,
  } = useApplicantStore();

  const {
    formState: { errors, isDirty, isValid },
    handleSubmit,
    register,
    setValue,
  } = useForm({
    criteriaMode: 'firstError',
    defaultValues: {
      cityValue: currentResidenceCityAsString,
      housingAmountValue: toCurrencyString(
        currentResidenceMonthlyPaymentAmount,
      ),
      lineOneValue: currentResidenceLineOneAsString,
      lineTwoValue: currentResidenceLineTwoAsString,
      postalCodeValue: currentResidencePostalCodeAsString,
      stateValue: currentResidenceStateAsString,
    },
    delayError: 200,
    mode: 'onTouched',
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const addressValidation = {
    required: t('validation.address.required'),
  };

  const housingAmountValidation = {
    setValueAs: (value: string): string => toNumericString(value),
    required: t('validation.housingAmount.required'),
    min: {
      value: 0,
      message: t('validation.housingAmount.min', { min: '$0' }),
    },
    max: {
      value: 1000000,
      message: t('validation.housingAmount.max', { max: '$1,000,000' }),
    },
  };

  const cityValidation = {
    required: t('validation.city.required'),
  };

  const stateValidation = {
    required: t('validation.state.required'),
  };

  const postalCodeValidation = {
    required: t('validation.zipCode.required'),
    minLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
    maxLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      cityValue,
      housingAmountValue,
      lineOneValue,
      lineTwoValue,
      postalCodeValue,
      stateValue,
    } = values;

    if (
      !cityValue ||
      !housingAmountValue ||
      !lineOneValue ||
      !postalCodeValue ||
      !stateValue
    ) {
      return;
    }

    setCurrentResidenceCity(cityValue);
    setCurrentResidenceLineOne(lineOneValue);
    setCurrentResidenceLineTwo(lineTwoValue);
    setCurrentResidenceMonthlyPaymentAmount(housingAmountValue);
    setCurrentResidencePostalCode(postalCodeValue);
    setCurrentResidenceState(stateValue);

    if (isCurrentResidencePersisted && isDirty) {
      const synced = await syncResidence();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="page applicant-residence-details-page">
        <Header
          progress={progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.applicantResidenceDetails.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicantResidenceDetails.form.h1}>
                {t('ApplicantResidenceDetailsPage.title')}
              </h1>
              <h2 data-testid={locator.pages.applicantResidenceDetails.form.h2}>
                {t('ApplicantResidenceDetailsPage.subtitle')}
              </h2>

              {!isEmpty(syncingResidenceFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.applicantResidenceDetails.alert.error
                    }
                    severity="error"
                  >
                    {syncingResidenceFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <NumberInput
                    {...register('housingAmountValue', housingAmountValidation)}
                    data-testid={
                      locator.pages.applicantResidenceDetails.form.amountInput
                    }
                    error={errors.housingAmountValue}
                    errorTestId={
                      locator.pages.applicantResidenceDetails.form
                        .amountInputError
                    }
                    label={t('ApplicantResidenceDetailsPage.housingAmount')}
                    currency
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <AddressLineOne
                    data-testid={
                      locator.pages.applicantResidenceDetails.form
                        .addressLine1Input
                    }
                    errorTestId={
                      locator.pages.applicantResidenceDetails.form
                        .addressLine1InputError
                    }
                    errors={errors}
                    label={t('ApplicantResidenceDetailsPage.residenceLineOne')}
                    name="lineOneValue"
                    register={register}
                    setValue={setValue}
                    suggestionFields={{
                      cityFieldName: 'cityValue',
                      stateFieldName: 'stateValue',
                      postalCodeFieldName: 'postalCodeValue',
                    }}
                    validation={addressValidation}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <Input
                        {...register('lineTwoValue')}
                        autoComplete="address-line2"
                        data-testid={
                          locator.pages.applicantResidenceDetails.form
                            .addressLine2Input
                        }
                        label={t(
                          'ApplicantResidenceDetailsPage.residenceLineTwo',
                        )}
                      />
                    </div>

                    <div className="input-half">
                      <Input
                        {...register('cityValue', cityValidation)}
                        autoComplete="address-level2"
                        data-testid={
                          locator.pages.applicantResidenceDetails.form.cityInput
                        }
                        error={errors.cityValue}
                        errorTestId={
                          locator.pages.applicantResidenceDetails.form
                            .cityInputError
                        }
                        label={t('ApplicantResidenceDetailsPage.residenceCity')}
                      />
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <Select
                        {...register('stateValue', stateValidation)}
                        error={errors.stateValue}
                        errorTestId={
                          locator.pages.applicantResidenceDetails.form
                            .stateSelectError
                        }
                        inputTestId={
                          locator.pages.applicantResidenceDetails.form
                            .stateSelect
                        }
                        label={t(
                          'ApplicantResidenceDetailsPage.residenceState',
                        )}
                        options={USStates.map(({ abbreviation, name }) => ({
                          name,
                          value: abbreviation,
                        }))}
                      />
                    </div>

                    <div className="input-half">
                      <NumberInput
                        {...register('postalCodeValue', postalCodeValidation)}
                        autoComplete="postal-code"
                        data-testid={
                          locator.pages.applicantResidenceDetails.form
                            .zipCodeInput
                        }
                        error={errors.postalCodeValue}
                        errorTestId={
                          locator.pages.applicantResidenceDetails.form
                            .zipCodeInputError
                        }
                        label={t('ApplicantResidenceDetailsPage.zip')}
                        type="number"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Button
                  data-testid={
                    locator.pages.applicantResidenceDetails.form.submitButton
                  }
                  disabled={!isValid}
                  loader={isSyncingResidence}
                  fullWidth
                  submit
                >
                  {t('ApplicantResidenceDetailsPage.continue')}
                </Button>
              </div>
            </div>
            <footer
              data-testid={locator.pages.applicantResidenceDetails.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantResidenceDetailsPage';
